import axios from 'axios';
import type { AxiosError, AxiosInstance, AxiosRequestTransformer, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import cookieStore from './cookie';
import { addBreadcrumb, captureTokenRefreshError } from '../plugins/sentry';
import { parseJwt } from './jwt';
import eventBus from '../helpers/eventBus';
import EventNames from '../models/eventNames';
import { getDeviceId } from '../plugins/nSure';
import { REFRESH_ERROR_MESSAGES, TokenRefreshError } from './tokenRefreshError';
import type { RefreshFailureReason } from './tokenRefreshError';

export {
  REFRESH_ERROR_MESSAGES,
  REPORTED_REFRESH_ERROR_MESSAGES,
  TokenRefreshError,
  isTokenRefreshError,
} from './tokenRefreshError';
export type { RefreshFailureReason } from './tokenRefreshError';

declare const APP_VERSION: string;

const KEYS = {
  JWT: '__jwt',
  JWT_EXPIRATION: '__jwt-exp',
  REFRESH_TOKEN: '__refresh_token',
  REFRESH_TOKEN_EXPIRATION: '__refresh_token-exp',
};

export interface TokenResponse {
  jwt: string;
  jwtExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
}

export interface SessionTimeoutPayload {
  reason: RefreshFailureReason;
}

// Statuses the auth service uses to reject a refresh token. 400 is deliberately absent:
// per the service contract it means our *request* was malformed, and it is also what
// nginx returns for "Request Header Or Cookie Too Large" — neither is evidence the
// token is dead, and acting on it logs out a user whose session was fine.
const DEFINITIVE_REJECTION_STATUSES = [401, 403];

const STALE_REFRESH_TOKEN_CODE = 'jwt-wrong-refresh-token';

const KNOWN_AUTH_ERROR_CODES = [
  STALE_REFRESH_TOKEN_CODE,
  'user-blocked',
  'authentication-required',
  'validation-failed',
];

const getResponseStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const response = (error as { response?: { status?: unknown } }).response;

  return typeof response?.status === 'number' ? response.status : undefined;
};

const getAuthServiceErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const data = (error as { response?: { data?: unknown } }).response?.data;

  if (typeof data !== 'object' || data === null) {
    return undefined;
  }

  const code = (data as { code?: unknown }).code;

  return typeof code === 'string' && '' !== code ? code : undefined;
};

const toRefreshCodeTag = (code: string | undefined): string => {
  if (code === undefined) {
    return 'none';
  }

  return KNOWN_AUTH_ERROR_CODES.includes(code) ? code : 'other';
};

const toCause = (error: unknown): Error | undefined => (error instanceof Error ? error : undefined);

const toTransformerList = (
  transformers: AxiosRequestTransformer | AxiosRequestTransformer[] | undefined,
): AxiosRequestTransformer[] => {
  if (Array.isArray(transformers)) {
    return transformers;
  }

  return transformers === undefined ? [] : [transformers];
};

const createRefreshClient = (): AxiosInstance => {
  const defaultTransformerList = toTransformerList(axios.defaults.transformRequest);

  const instance = axios.create({
    transformRequest: [
      ((data, headers) => {
        const headersWithCommon = headers as typeof headers & { common?: { Authorization?: string } };
        if (typeof headersWithCommon === 'object' && headersWithCommon?.common) {
          delete headersWithCommon.common.Authorization;
        }

        return data;
      }) as AxiosRequestTransformer,
      ...defaultTransformerList,
    ],
  });

  axiosRetry(instance, {
    retries: 2,
    retryDelay: retryCount => retryCount * 500 + Math.floor(Math.random() * 250),
    // Retry only 5xx: the server received the call, so the refresh token was not
    // consumed. A no-response network error is deliberately NOT retried here — the
    // server may have already rotated the refresh token, and re-sending the stale
    // one would burn the rotation; the caller re-attempts on demand instead.
    retryCondition: (error: AxiosError): boolean => {
      const status = error.response?.status;

      return status !== undefined && status >= 500;
    },
  });

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      const deviceId = await getDeviceId();

      config.headers['X-Device-ID'] = deviceId || '';
      config.headers['X-PB-User-Agent'] = `pb-front/${APP_VERSION}`;

      return config;
    },
    (error: unknown) => Promise.reject(error),
  );

  return instance;
};

let refreshPromise: Promise<TokenResponse> | null = null;

const EXCESSIVE_REFRESH_WINDOW_MS = 10_000;
const EXCESSIVE_REFRESH_THRESHOLD = 5;

interface RefreshAttempt {
  timestamp: number;
  failedUrl: string | null;
}

let refreshAttempts: RefreshAttempt[] = [];
let excessiveRefreshLoggedInWindow = false;
let throttledRefreshReported = false;

const countRecentRefreshAttempts = (): number => {
  const now = Date.now();

  return refreshAttempts.filter(
    attempt => now - attempt.timestamp < EXCESSIVE_REFRESH_WINDOW_MS,
  ).length;
};

const collectFailedUrls = (): string[] => refreshAttempts
  .map(attempt => attempt.failedUrl)
  .filter((url): url is string => url !== null);

export default {
  trackRefreshAttempt(failedUrl?: string): void {
    const now = Date.now();
    refreshAttempts = refreshAttempts.filter(
      attempt => now - attempt.timestamp < EXCESSIVE_REFRESH_WINDOW_MS,
    );

    if (refreshAttempts.length < EXCESSIVE_REFRESH_THRESHOLD) {
      excessiveRefreshLoggedInWindow = false;
    }

    refreshAttempts.push({
      timestamp: now,
      failedUrl: failedUrl || null,
    });

    if (refreshAttempts.length < EXCESSIVE_REFRESH_THRESHOLD || excessiveRefreshLoggedInWindow) {
      return;
    }

    excessiveRefreshLoggedInWindow = true;

    addBreadcrumb({
      message: 'Excessive JWT refresh requests detected',
      data: {
        failedUrl: failedUrl || null,
        failedUrls: collectFailedUrls(),
        refreshCount: refreshAttempts.length,
      },
    });
  },

  getToken(): string | undefined {
    return cookieStore.get(KEYS.JWT);
  },

  hasToken(): boolean {
    return cookieStore.get(KEYS.JWT) !== undefined;
  },

  getRefreshToken(): string | undefined {
    return cookieStore.get(KEYS.REFRESH_TOKEN);
  },

  hasRefreshToken(): boolean {
    return cookieStore.get(KEYS.REFRESH_TOKEN) !== undefined;
  },

  getUserId(): string | undefined {
    return this.getUser()?.id;
  },

  getUser(): { id?: string; email?: string } | null {
    const token = this.getToken();
    if (token === undefined) {
      return null;
    }

    const payload = parseJwt(token);
    if (payload === null) {
      return null;
    }

    return {
      id: payload.sub,
      email: payload.email,
    };
  },

  setTokens({ jwt, jwtExpiresAt, refreshToken, refreshTokenExpiresAt }: TokenResponse): void {
    cookieStore.set(KEYS.JWT, jwt, {
      expires: new Date(refreshTokenExpiresAt),
    });
    cookieStore.set(KEYS.REFRESH_TOKEN, refreshToken, {
      expires: new Date(refreshTokenExpiresAt),
    });
    cookieStore.set(KEYS.JWT_EXPIRATION, String(jwtExpiresAt), {
      expires: new Date(refreshTokenExpiresAt),
    });
    cookieStore.set(KEYS.REFRESH_TOKEN_EXPIRATION, String(refreshTokenExpiresAt), {
      expires: new Date(refreshTokenExpiresAt),
    });
  },

  removeTokens(): void {
    cookieStore.remove(KEYS.JWT);
    cookieStore.remove(KEYS.JWT_EXPIRATION);
    cookieStore.remove(KEYS.REFRESH_TOKEN);
    cookieStore.remove(KEYS.REFRESH_TOKEN_EXPIRATION);
  },

  getTokensFromCookies(): TokenResponse | null {
    const jwt = cookieStore.get(KEYS.JWT);
    const refreshToken = cookieStore.get(KEYS.REFRESH_TOKEN);

    if (jwt === undefined || refreshToken === undefined) {
      return null;
    }

    return {
      jwt,
      jwtExpiresAt: cookieStore.get(KEYS.JWT_EXPIRATION) ?? '',
      refreshToken,
      refreshTokenExpiresAt: cookieStore.get(KEYS.REFRESH_TOKEN_EXPIRATION) ?? '',
    };
  },

  requestNewToken(failedUrl?: string): Promise<TokenResponse> {
    if (refreshPromise !== null) {
      return refreshPromise;
    }
    if (!this.hasRefreshToken()) {
      const timeoutPayload: SessionTimeoutPayload = { reason: 'no-refresh-token' };
      eventBus.emit(EventNames.SESSION_TIMEOUT, timeoutPayload);

      return Promise.reject(
        new TokenRefreshError(REFRESH_ERROR_MESSAGES.noRefreshToken, { reason: 'no-refresh-token' }),
      );
    }

    const recentRefreshAttempts = countRecentRefreshAttempts();

    if (recentRefreshAttempts >= EXCESSIVE_REFRESH_THRESHOLD) {
      const throttledError = new TokenRefreshError(REFRESH_ERROR_MESSAGES.throttled, { reason: 'transient' });

      if (!throttledRefreshReported) {
        throttledRefreshReported = true;
        captureTokenRefreshError({
          error: throttledError,
          tags: { 'auth.refresh_reason': 'throttled' },
          extra: {
            failedUrl: failedUrl || null,
            failedUrls: collectFailedUrls(),
            recentRefreshAttempts,
          },
        });
      }

      return Promise.reject(throttledError);
    }

    this.trackRefreshAttempt(failedUrl);

    const refreshAttemptsAtStart = countRecentRefreshAttempts();

    const sentRefreshToken = this.getRefreshToken();

    const instance = createRefreshClient();

    refreshPromise = instance
      .post<TokenResponse>(
        '/public/authentication-service/v1/jwt/refresh',
        {
          refreshToken: sentRefreshToken,
        },
      )
      .then(({ data }: AxiosResponse<TokenResponse>) => {
        this.setTokens(data);

        return data;
      })
      .catch((error: unknown) => {
        const status = getResponseStatus(error);

        const report = (refreshError: TokenRefreshError, extra: Record<string, unknown>): void => {
          captureTokenRefreshError({
            error: refreshError,
            tags: {
              'auth.refresh_reason': refreshError.reason,
              'auth.refresh_code': toRefreshCodeTag(refreshError.authCode),
            },
            extra: {
              hasJwt: this.hasToken(),
              refreshTokenLength: sentRefreshToken?.length ?? 0,
              recentRefreshAttempts: refreshAttemptsAtStart,
              errorExtraMessage: 'Error in refresh method for token',
              ...extra,
            },
          });
        };

        const destroySession = (refreshError: TokenRefreshError): TokenRefreshError => {
          const timeoutPayload: SessionTimeoutPayload = { reason: refreshError.reason };

          this.removeTokens();
          eventBus.emit(EventNames.SESSION_TIMEOUT, timeoutPayload);

          return refreshError;
        };

        const authCode = getAuthServiceErrorCode(error);

        // Only a rejection the auth service itself sent (its body always carries a `code`)
        // proves the refresh token is dead. Everything else — network gap, 5xx, 400, or a
        // 401/403 from a WAF/proxy with no auth-service body — keeps the cookies so the
        // next on-demand attempt can succeed. Expected on flaky mobile networks and not a
        // defect, so it is never reported; callers must not report it either.
        if (status === undefined || !DEFINITIVE_REJECTION_STATUSES.includes(status) || authCode === undefined) {
          if (status !== undefined && DEFINITIVE_REJECTION_STATUSES.includes(status)) {
            addBreadcrumb({
              message: 'JWT refresh rejected without an auth-service response',
              data: { httpStatus: status },
            });
          }

          throw new TokenRefreshError(REFRESH_ERROR_MESSAGES.transient, {
            reason: 'transient',
            httpStatus: status,
            cause: toCause(error),
          });
        }

        // A definitive rejection can also mean a *concurrent* refresh (another
        // axios instance / recovery path) already rotated the token: the cookie
        // now holds a different, valid refresh token than the one we sent. In
        // that case the session is fine — recover with the rotated tokens instead
        // of destroying it. Only log out when the sent token is still current.
        const currentRefreshToken = this.getRefreshToken();
        if (currentRefreshToken !== undefined && currentRefreshToken !== sentRefreshToken && this.hasToken()) {
          const rotatedTokens = this.getTokensFromCookies();
          if (rotatedTokens !== null) {
            return rotatedTokens;
          }
        }

        if ('user-blocked' === authCode) {
          throw destroySession(
            new TokenRefreshError(REFRESH_ERROR_MESSAGES.userBlocked, {
              reason: 'user-blocked',
              httpStatus: status,
              authCode,
              cause: toCause(error),
            }),
          );
        }

        const rejected = new TokenRefreshError(
          REFRESH_ERROR_MESSAGES.rejected,
          { reason: 'auth-rejected', httpStatus: status, authCode, cause: toCause(error) },
        );

        // A lone stale-token rejection is a session that legitimately ended (logged out
        // elsewhere, expired, revoked). A burst of refreshes, or any other code, points
        // at us, e.g. `validation-failed` on a missing device ID.
        if (refreshAttemptsAtStart > 1 || STALE_REFRESH_TOKEN_CODE !== authCode) {
          report(rejected, { rawAuthCode: authCode });
        }

        throw destroySession(rejected);
      })
      .finally(() => {
        refreshPromise = null;
      });

    return refreshPromise;
  },

  // Test helper to clear module-level refresh tracking between specs.
  _resetRefreshTrackingForTests(): void {
    refreshAttempts = [];
    excessiveRefreshLoggedInWindow = false;
    throttledRefreshReported = false;
    refreshPromise = null;
  },
};
